import { DatePipe } from '@angular/common'
import { Component, EventEmitter, Output } from '@angular/core'
import { RouterLink } from '@angular/router'

import { BaseComponent, FeatureType } from '../../shared'

export interface JobDueForJhaRow {
    jobId: string
    jobName: string
    siteSuperName: string
    isDue: boolean
    dueReasons: string[]
    lastJhaDate: string | null
    lastJhaId: string | null
    daysSinceLastJha: number | null
    hoursSinceLastJha: number
    currentLaborHours: number
    hoursUntilDue: number
    dueByDate: string | null
}

interface JobsDueForJhaResponse {
    totalDueCount: number
    jobs: JobDueForJhaRow[]
}

interface JhaDashboardKpi {
    jhasSubmittedLast30Days: number
    openNonCompliantItems: number
    itemsMarkedCorrectedLast90Days: number
    submittedJhasLast6Months: number
}

interface JhaOpenNcByJobRow {
    jobId: string
    jobName: string
    openItems: number
    focusJhaId: string | null
}

interface JhaMonthlyCountRow {
    month: string
    count: number
}

interface JhaSubmissionHeatmapRow {
    label: string
    values: number[]
}

interface JhaSubmissionHeatmap {
    months: string[]
    rows: JhaSubmissionHeatmapRow[]
}

interface JhaCorrectiveAgingRow {
    jobId: string
    jobName: string
    siteSuper: string
    daysOpen: number
    openItemCount: number
    focusJhaId: string | null
}

interface JhaDashboardMetrics {
    kpi: JhaDashboardKpi
    openNcByJob: JhaOpenNcByJobRow[]
    correctionsByMonth: JhaMonthlyCountRow[]
    submissionHeatmap: JhaSubmissionHeatmap
    correctiveAging: JhaCorrectiveAgingRow[]
}

const emptyMetrics = (): JhaDashboardMetrics => ({
    kpi: {
        jhasSubmittedLast30Days: 0,
        openNonCompliantItems: 0,
        itemsMarkedCorrectedLast90Days: 0,
        submittedJhasLast6Months: 0,
    },
    openNcByJob: [],
    correctionsByMonth: [],
    submissionHeatmap: { months: [], rows: [] },
    correctiveAging: [],
})

@Component({
    selector: 'app-job-hazard-analysis-dashboard',
    templateUrl: './job-hazard-analysis-dashboard.component.html',
    styleUrls: ['./job-hazard-analysis-dashboard.component.css'],
    standalone: true,
    imports: [RouterLink, DatePipe],
})
export class JobHazardAnalysisDashboardComponent extends BaseComponent {
    @Output() addForJob = new EventEmitter<{ jobId: string; jobName: string }>()

    jobsDueForJha: JobDueForJhaRow[] = []
    dueCount = 0
    dueQueueLoadError = false
    metricsLoadError = false
    metrics: JhaDashboardMetrics = emptyMetrics()

    constructor() {
        super()
        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            'jhaDashboard',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get<JobsDueForJhaResponse>('/api/JobHazardAnalysis/GetJobsDueForJha'),
                    this.services.data.get<JhaDashboardMetrics>('/api/JobHazardAnalysis/GetDashboardMetrics'),
                )
                .subscribe({
                    next: results => {
                        const dueResponse = results[0]
                        const metricsResponse = results[1]

                        this.jobsDueForJha = dueResponse?.jobs ?? []
                        this.dueCount = dueResponse?.totalDueCount ?? this.jobsDueForJha.length
                        this.dueQueueLoadError = false

                        this.metrics = metricsResponse ?? emptyMetrics()
                        this.metricsLoadError = false
                        this.initialLoadingComplete()
                    },
                    error: () => {
                        this.jobsDueForJha = []
                        this.dueCount = 0
                        this.dueQueueLoadError = true
                        this.metrics = emptyMetrics()
                        this.metricsLoadError = true
                        this.initialLoadingComplete()
                    },
                }),
        )
    }

    get canViewJhaList(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyJobHazardAnalysisView])
    }

    get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyJobHazardAnalysisEdit])
    }

    jhaListQueryParamsForFocusJha(focusJhaId: string | null): Record<string, string> | null {
        if (focusJhaId) {
            return { openJhaId: focusJhaId }
        }
        return null
    }

    formatJobDisplay(jobId: string, jobName: string): string {
        if (!jobName || jobName === jobId) {
            return jobId
        }
        return `${jobId} — ${jobName}`
    }

    formatDueReasons(reasons: string[]): string {
        return (reasons ?? [])
            .map(reason => {
                switch (reason) {
                    case 'NoPriorJha':
                        return 'No submitted JHA on file'
                    case 'TimeThreshold':
                        return '1+ calendar month since last JHA'
                    case 'HoursThreshold':
                        return '1,200+ labor hours since last JHA'
                    default:
                        return reason
                }
            })
            .join('; ')
    }

    isUrgentDueRow(row: JobDueForJhaRow): boolean {
        return row.dueReasons.includes('NoPriorJha') || row.dueReasons.length > 1
    }

    formatHours(value: number): string {
        return Math.round(value ?? 0).toLocaleString()
    }

    maxResolvedCount(): number {
        return Math.max(...this.metrics.correctionsByMonth.map(m => m.count), 1)
    }

    heatmapColor(value: number): string {
        if (value <= 0) {
            return '#f8f9fa'
        }
        const t = Math.min(value / 5, 1)
        return `rgba(13, 110, 253, ${0.12 + t * 0.78})`
    }
}
